import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  DataTable,
  DateUI,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { neverFunc } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { RiBuilding2Line, RiFilter3Line, RiPriceTagLine } from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import { assetRuleTypeValues } from "~/gql/client/enumValues";
import type {
  AdminAssetRulesRowFragment,
  AssetRulesPageFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const TYPE_LABELS: Record<(typeof assetRuleTypeValues)[number], string> = {
  TOP_UP: "Top-up",
  ALERT: "Alert",
  RECURRING_TRANSFER: "Recurring transfer",
  RECURRING_WITHDRAWAL: "Recurring withdrawal",
  SWEEP: "Sweep",
};

const adminAssetRulesDocument = graphql(`
  query AdminAssetRules($after: String, $first: Int!, $filter: AssetRulesPageFilter!) {
    assetRules(after: $after, first: $first, filter: $filter) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      assetRules {
        ...AdminAssetRulesRow
      }
    }
  }

  fragment AdminAssetRulesRow on AssetRule {
    id
    name
    status
    evaluationStatus
    nextEvaluationDueAt
    asset {
      id
      businessEntity {
        organisation {
          id
          name
          squareLogoUrl
        }
      }
      ...AssetCardFields
    }
    ... on RecurringWithdrawalAssetRule {
      beneficiaryAccount {
        id
        organisation {
          id
          name
          squareLogoUrl
        }
        ...BeneficiaryAssetCardFields
      }
    }
  }
`);

const DATA_TABLE_PAGE_SIZE = 10_000;

export const Route = createFileRoute("/__auth/asset-rules")({
  head: () => opsHead("Asset Rules"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const columns: ColumnDef<AdminAssetRulesRowFragment>[] = [
  {
    id: "organisation",
    header: "Organisation",
    cell: ({ row }) => {
      const org =
        row.original.asset?.businessEntity?.organisation ??
        (row.original.__typename === "RecurringWithdrawalAssetRule"
          ? row.original.beneficiaryAccount?.organisation
          : undefined);
      return org ? (
        <div className="max-w-48">
          <OrganisationCard organisation={org} />
        </div>
      ) : (
        <Txt variant="caption" tone="secondary">
          —
        </Txt>
      );
    },
  },
  {
    id: "account",
    header: "Account",
    cell: ({ row }) => {
      if (row.original.asset) {
        return (
          <AccountCard
            account={row.original.asset}
            subText={["BUSINESS_ENTITY", "BALANCE"]}
          />
        );
      }

      if (
        row.original.__typename === "RecurringWithdrawalAssetRule" &&
        row.original.beneficiaryAccount
      ) {
        return (
          <AccountCard
            account={row.original.beneficiaryAccount}
            subText="BUSINESS_ENTITY"
          />
        );
      }

      return "—";
    },
  },
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => row.original.name,
  },
  {
    id: "type",
    header: "Type",
    cell: ({ row }) => {
      switch (row.original.__typename) {
        case "TopUpAssetRule":
          return "Top-up";
        case "AlertAssetRule":
          return "Alert";
        case "RecurringTransferAssetRule":
          return "Recurring transfer";
        case "RecurringWithdrawalAssetRule":
          return "Recurring withdrawal";
        case "SweepAssetRule":
          return "Sweep";
        default:
          return neverFunc(row.original);
      }
    },
  },
  {
    id: "nextEvaluationDueAt",
    header: "Next evaluation",
    cell: ({ row }) => (
      <Txt variant="body" nowrap>
        <DateUI date={row.original.nextEvaluationDueAt} />
      </Txt>
    ),
  },
  {
    id: "evaluationStatus",
    header: "Eval health",
    cell: ({ row }) =>
      row.original.evaluationStatus ? (
        <StatusUI status={row.original.evaluationStatus} />
      ) : (
        <Txt variant="caption" tone="secondary">
          —
        </Txt>
      ),
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => <StatusUI status={row.original.status} />,
  },
];

function View() {
  const { useSearchParams } = useRouter();

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      organisationIds: z.array(z.string()).optional().nullable(),
      types: z.array(z.enum(assetRuleTypeValues)).optional().nullable(),
    }),
    defaultState: {},
  });

  const baseInput = {
    first: 50,
    filter: { ...filter, status: ["ACTIVE" as const] },
  };

  const { data, fetchMore, loading, networkStatus } = useQuery(
    adminAssetRulesDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const { data: pageData } = useSuspenseQuery(
    graphql(`
      query AdminAssetRulesPage {
        organisations {
          id
          name
        }
      }
    `),
  );

  const organisationOptions =
    pageData?.organisations?.map((o) => ({
      value: o.id,
      label: o.name,
    })) ?? [];

  const evalStatusOrder: Record<string, number> = {
    USER_REVIEW_REQUIRED: 0,
    IN_PROGRESS: 1,
    BREACH: 2,
    HEALTHY: 3,
  };

  const assetRules = [...(data?.assetRules?.assetRules ?? [])].sort((a, b) => {
    const aOrder = a.evaluationStatus
      ? (evalStatusOrder[a.evaluationStatus] ?? 3)
      : 3;
    const bOrder = b.evaluationStatus
      ? (evalStatusOrder[b.evaluationStatus] ?? 3)
      : 3;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    const aNext = a.nextEvaluationDueAt
      ? new Date(a.nextEvaluationDueAt).getTime()
      : Infinity;
    const bNext = b.nextEvaluationDueAt
      ? new Date(b.nextEvaluationDueAt).getTime()
      : Infinity;
    return aNext - bNext;
  });
  const nextCursor = data?.assetRules?.pageInfo.hasNextPage
    ? (data.assetRules.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <AdminPageContainer testId="admin-asset-rules">
      <AdminPageHeader title="Asset Rules" subtitle="Active rules" />

      <FilterBar
        filterValue={filter}
        onFilterChange={setFilter}
        organisationOptions={organisationOptions}
      />

      <DataTable
        data={assetRules}
        columns={columns}
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        manualPagination
        hidePagination
        pageSize={DATA_TABLE_PAGE_SIZE}
        className="overflow-hidden border"
        linkTo={(row) => `/asset-rules/${row.original.id}`}
      />

      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({
                variables: {
                  after: nextCursor,
                  ...baseInput,
                },
              })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!assetRules.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </AdminPageContainer>
  );
}

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
}: {
  filterValue: AssetRulesPageFilter;
  onFilterChange: (_: AssetRulesPageFilter) => void;
  organisationOptions: {
    value: string;
    label: React.ReactNode;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("type");

  const filterCount =
    ((filterValue?.types?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.organisationIds?.length ?? 0) > 0 ? 1 : 0);

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      types: undefined,
      organisationIds: undefined,
    });
  };

  const typeOptions = assetRuleTypeValues.map((t) => ({
    value: t,
    label: <span>{TYPE_LABELS[t]}</span>,
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("type")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Type</div>
                {(filterValue.types?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("organisationId")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "type" && (
              <ControlledFacetedFilterControl
                options={typeOptions}
                selectedValues={filterValue.types ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    types: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "organisationId" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledFactedFilterSummary
        title="Type"
        options={typeOptions}
        selectedValues={filterValue.types ?? []}
        onClick={() => {
          setPath("type");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("organisationId");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
